import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageSquarePlus, Instagram, Youtube } from "lucide-react";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useAuth } from "@/hooks/useAuth";
import { TicketWall } from "@/components/guest/TicketWall";
import { openCookieSettings } from "@/components/CookieConsent";
import tmdbLogo from "@/assets/tmdb-logo.svg";

const SOCIAL_LINKS = [
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@swiftix.app",
    icon: (props: { className?: string }) => (
      <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.42a8.16 8.16 0 0 0 4.77 1.52V7.5a4.85 4.85 0 0 1-1.84-.81z" />
      </svg>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@swiftixapp",
    icon: (props: { className?: string }) => <Youtube className={props.className} aria-hidden="true" />,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/swiftix.app/",
    icon: (props: { className?: string }) => <Instagram className={props.className} aria-hidden="true" />,
  },
];

export const Footer = () => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { user } = useAuth();

  return (
    <>
    {user && <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />}
    <footer className="border-t border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 mt-auto">
      <div className="container px-4 py-6 sm:py-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand */}
          <div className="space-y-3">
            <h3 className="font-bold text-lg gradient-text">Swiftix</h3>
            <p className="text-sm text-muted-foreground">
              Your seat at the movies. Discover, plan, gather, and remember.
            </p>

          </div>

          {/* Product */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Product</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/movie-night" className="hover:text-accent transition-colors">
                  Movie Night
                </Link>
              </li>
              <li>
                <Link to="/watch-planner" className="hover:text-accent transition-colors">
                  Watch Planner
                </Link>
              </li>
              <li>
                <Link to="/collections" className="hover:text-accent transition-colors">
                  Collections
                </Link>
              </li>
              <li>
                <Link to="/vip" className="hover:text-accent transition-colors">
                  Swiftix VIP
                </Link>
              </li>

            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Legal</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/privacy" className="hover:text-accent transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-accent transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="hover:text-accent transition-colors">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-accent transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <button onClick={openCookieSettings} className="hover:text-accent transition-colors text-left">
                  Cookie settings
                </button>
              </li>
              <li>
                <Link to="/privacy#delete" className="hover:text-accent transition-colors">
                  Delete my data
                </Link>
              </li>
              <li>
                <Link to="/support" className="hover:text-accent transition-colors">
                  Box Office (Support)
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setFeedbackOpen(true)}
                  className="hover:text-accent transition-colors text-left"
                >
                  Leave feedback
                </button>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Community</h4>
            <p className="text-sm text-muted-foreground">
              Follow us for updates and movie recommendations
            </p>
            <div className="flex items-center gap-2">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Swiftix on ${label}`}
                  className="grid h-9 w-9 place-items-center rounded-md border border-border/50 bg-background/60 text-muted-foreground transition hover:border-primary/60 hover:text-primary"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <TicketWall action="send feedback">
            <button
              onClick={() => setFeedbackOpen(true)}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <MessageSquarePlus className="h-4 w-4" />
              Send Feedback
            </button>
            </TicketWall>
          </div>

        </div>

        <div className="mt-8 pt-8 border-t border-border/40 text-center text-sm text-muted-foreground">
          <a
            href="https://www.themoviedb.org"
            target="_blank"
            rel="noopener noreferrer"
            className="mx-auto mb-4 flex max-w-md flex-col items-center gap-2 text-xs opacity-80 transition hover:opacity-100"
          >
            <img src={tmdbLogo} alt="TMDB" width={110} height={14} loading="lazy" className="h-3.5 w-auto" />
            <span>This product uses the TMDB API but is not endorsed or certified by TMDB.</span>
          </a>
          <p className="flex items-center justify-center gap-1">
            Made with <Heart className="h-4 w-4 text-red-500 fill-red-500" /> for movie lovers
          </p>
          <p className="mt-2">© {new Date().getFullYear()} Swiftix. All rights reserved.</p>
        </div>
      </div>
    </footer>
    </>
  );
};
